import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';

const { FiShield, FiSearch, FiMonitor, FiMapPin, FiClock, FiActivity, FiArrowDownCircle, FiFilter } = FiIcons;

const SecurityLogs = () => {
  const { data } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const logs = (data.accessLogs || [])
    .filter(log => {
      const matchesSearch = log.event.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           log.userId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || log.event.toLowerCase().includes(filterType.toLowerCase());
      return matchesSearch && matchesFilter;
    });

  const getEventStyles = (event) => {
    if (event.includes('Login')) return 'bg-green-50 text-green-700 border-green-100';
    if (event.includes('Delete')) return 'bg-red-50 text-red-700 border-red-100';
    if (event.includes('Export')) return 'bg-blue-50 text-blue-700 border-blue-100';
    return 'bg-gray-50 text-gray-700 border-gray-100';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight flex items-center gap-2">
            <SafeIcon icon={FiShield} className="text-indigo-600" /> Security Audit Trail
          </h2>
          <p className="text-sm text-gray-500 font-medium italic">Monitoring platform integrity and data access.</p>
        </div>
        <div className="flex items-center gap-3">
           <div className="bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Real-time Logging Active</span>
           </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search by event or user..." 
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
           <SafeIcon icon={FiFilter} className="text-gray-400" />
           <select 
             className="bg-gray-50 border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
             value={filterType}
             onChange={e => setFilterType(e.target.value)}
           >
             <option value="all">All Events</option>
             <option value="Login">Logins</option>
             <option value="Delete">Deletions</option>
             <option value="Export">Data Exports</option>
           </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Timestamp</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Event</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">User</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Device / IP</th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-xs font-bold text-gray-900">
                      <SafeIcon icon={FiClock} className="text-gray-300" />
                      {format(parseISO(log.timestamp), 'MMM dd, HH:mm:ss')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${getEventStyles(log.event)}`}>
                      {log.event}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs font-black text-gray-700 flex items-center gap-1.5">
                       <div className="w-6 h-6 bg-indigo-50 text-indigo-600 rounded flex items-center justify-center text-[10px]">
                          {log.userId[0]}
                       </div>
                       {log.userId}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1 text-[10px] font-bold text-gray-500 uppercase tracking-tight">
                        <SafeIcon icon={FiMonitor} size={10} /> {log.device}
                      </div>
                      <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400">
                        <SafeIcon icon={FiMapPin} size={10} /> {log.ip}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${log.status === 'Success' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-[10px] font-black text-gray-900 uppercase">{log.status}</span>
                    </div>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <SafeIcon icon={FiActivity} size={32} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No matching security logs</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-gray-50/30 border-t border-gray-100 flex justify-between items-center">
          <p className="text-[10px] font-bold text-gray-400 uppercase">Showing {logs.length} Audit Entries</p>
          <button className="text-[10px] font-black text-indigo-600 uppercase flex items-center gap-1 hover:underline">
             <SafeIcon icon={FiArrowDownCircle} /> Archive Logs (CSV)
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityLogs;